import axios, { InternalAxiosRequestConfig } from 'axios';

const API_URL = 'https://apiwhomii-z2uc5.ondigitalocean.app';

interface UserData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  token?: string;
}

interface AuthResponse {
  token: string;
  [key: string]: any;
}

interface Module {
  moduleId: number;
  translationList: {
    translationId: number;
    languageCode: string;
    name: string;
    title: string;
    description: string;
    createdAt: string;
    updatedAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
  testList: {
    [key: string]: {
      en: string;
      tr: string;
    };
  }[];
}

interface QuestionContent {
  languageCode: string;
  contentId: number;
  mechanic: {
    name: string;
    id: number;
    translationList: {
      description: string | null;
      language: {
        code: string;
        name: string;
        id: number;
      };
      id: number;
    }[];
  };
  textContent: string;
  filePath: string;
  createdAt: string;
  updatedAt: string;
}

interface Question {
  questionId: number;
  questionType: string;
  testId: number;
  serial: number;
  multipleChoice: boolean;
  maxChoice: number;
  extraQuestion: boolean;
  createdAt: string;
  updatedAt: string;
  questionContentList: QuestionContent[];
  answerPresent: boolean;
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const userDataStr = localStorage.getItem('whomii_user');
    if (userDataStr) {
      const userData: UserData = JSON.parse(userDataStr);
      
      // Skip Basic Auth for login and register routes
      if (config.url && 
         !config.url.includes('/auth/v1/login') && 
         !config.url.includes('/auth/v1/register')) {
        // Add Basic Auth header
        const basicAuth = btoa(`${userData.email}:${userData.password}`);
        config.headers.Authorization = `Basic ${basicAuth}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const registerRandomUser = async (): Promise<AuthResponse> => {
  // Generate random username, email and password
  const randomString = Math.random().toString(36).substring(2, 10);
  const userData: UserData = {
    username: `user_${randomString}`,
    email: `user_${randomString}@whomii.com`,
    password: `pass_${randomString}`,
    confirmPassword: `pass_${randomString}`
  };

  try {
    const response = await api.post<AuthResponse>('/auth/v1/register', userData);
    
    // Create complete user data object with all necessary fields
    const completeUserData = {
      username: userData.username,
      email: userData.email,
      password: userData.password,
      ...response.data
    };
    
    // Immediately save to localStorage
    localStorage.setItem('whomii_user', JSON.stringify(completeUserData));
    
    return response.data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const loginUser = async (email: string, password: string): Promise<AuthResponse> => {
  try {
    const response = await api.post<AuthResponse>('/auth/v1/login', { email, password });
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const getModules = async (lang = 'en'): Promise<Module[]> => {
  try {
    // Get user data for authentication
    const userDataStr = localStorage.getItem('whomii_user');
    if (!userDataStr) {
      throw new Error('Authentication required. Please log in first.');
    }
    
    const userData = JSON.parse(userDataStr);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add Basic Auth header
    if (userData.email && userData.password) {
      const basicAuth = btoa(`${userData.email}:${userData.password}`);
      headers.Authorization = `Basic ${basicAuth}`;
    } else if (userData.token) {
      // Fallback to Bearer token if available
      headers.Authorization = `Bearer ${userData.token}`;
    } else {
      throw new Error('No authentication credentials found');
    }
    
    const response = await axios.get<Module[]>(`${API_URL}/api/v1/modules`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching modules:', error);
    throw error;
  }
};

export const getModuleDetails = async (moduleId: string | number, lang = 'en'): Promise<Module> => {
  try {
    const response = await api.get<Module>(`/api/v1/modules/${moduleId}?lang=${lang.toUpperCase()}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching module ${moduleId}:`, error);
    throw error;
  }
};

export const getTestQuestions = async (testId: string | number): Promise<Question[]> => {
  try {
    const response = await api.get<Question[]>(`/api/v1/tests/${testId}/questions`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching questions for test ${testId}:`, error);
    throw error;
  }
};

export interface AnswerContent {
  contentId: number;
  answerId: number;
  languageCode: string;
  mechanic: {
    [key: string]: string;
  };
  textContent: string;
  filePath: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Answer {
  answerId: number;
  questionId: number;
  serial: number;
  nextQuestionId: number | null;
  createdAt: string;
  updatedAt: string;
  answerContentList: AnswerContent[];
  numericScore: number;
  correct: boolean;
}

export const getQuestionAnswers = async (questionId: number): Promise<Answer[]> => {
  try {
    const response = await api.get<Answer[]>(`/api/v1/questions/${questionId}/answers`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching answers for question ${questionId}:`, error);
    throw error;
  }
};

export default api; 